import { IField } from "@gtmhub/entities/models/models";
import { IInsightQueryCondition, IInsightQueryLayout, SupportedDataType, SupportedOperators } from "@gtmhub/insights";
import { Insight } from "@webapp/data-story/models/insights.models";
import { Parameter } from "@webapp/data-story/models/parameters-facade.models";
import { localize } from "@webapp/localization/utils/localization.utils";
import dayjs from "@webapp/shared/libs/dayjs";

export const POWER_BI_CONNECTION_NAME = "powerbi";

export const SQL_CONDITION_STRING_ARRAY_SEPARATOR = ", ";

export const getInsightUsedFilters = (insight: Insight, parameters: Parameter[]): Parameter[] => {
  return parameters.filter((parameter) => insight.usedFilters.includes(parameter.key));
};

export const convertPeriodStringToDateArray = (periodString: string): Date[] => {
  const [startDate, endDate] = periodString.split("|");

  return [dayjs(startDate).toDate(), dayjs(endDate).toDate()];
};

export const convertDateRangeObjectToString = ([startDate, endDate]: Date[]): string => `${dayjs(startDate).toISOString()}|${dayjs(endDate).toISOString()}`;

const supportedOperatorTranslationKeys: { [key in SupportedOperators]?: string } = {
  contains: "contains",
  doesNotContain: "does_not_contain",
  overlaps: "overlaps",
  doesNotOverlap: "does_not_overlap",
  equals: "equals_to",
  notEqual: "is_not_equal_to",
  hasLengthOf: "has_length_of",
  isContainedBy: "is_contained_by",
  isNotContainedBy: "is_not_contained_by",
  shorter: "is_shorter_than",
  longer: "is_longer_than",
};

const getInsightOperatorTranslationKey = (operator: SupportedOperators, dataType: SupportedDataType): string => {
  if (operator === SupportedOperators.Greater) {
    return dataType === SupportedDataType.Timestamp ||
      dataType === SupportedDataType.Date ||
      dataType === SupportedDataType.DateTime ||
      dataType === SupportedDataType.UnixTime
      ? "is_after"
      : "is_greater_than";
  } else if (operator === SupportedOperators.Less) {
    return dataType === SupportedDataType.Timestamp ||
      dataType === SupportedDataType.Date ||
      dataType === SupportedDataType.DateTime ||
      dataType === SupportedDataType.UnixTime
      ? "is_before"
      : "is_less_than";
  }

  return supportedOperatorTranslationKeys[operator] || "equals";
};

export const localizeInsightOperator = (operator: SupportedOperators, dataType: SupportedDataType): string =>
  localize(getInsightOperatorTranslationKey(operator, dataType));

export const compareFieldsByName = (a: IField, b: IField): boolean => a?.name === b?.name;

// Insight can include functions, which are not copyable with structuredClone
export const cloneInsight = (insight: Insight): Insight => {
  if (!insight) {
    return null;
  }

  const result: Partial<Insight> = {};

  // Copy everything except queryLayout
  Object.keys(insight).forEach((key) => {
    if (key === "queryLayout") {
      return;
    }

    result[key] = structuredClone(insight[key]);
  });

  const queryLayout: Partial<IInsightQueryLayout> = {};

  // Copy queryLayout, but use the same templateFunc in conditions
  Object.keys(insight.queryLayout ?? {}).forEach((key) => {
    if (key === "conditions") {
      const resultConditions: Partial<IInsightQueryCondition> = {};

      const { conditions } = insight.queryLayout;

      Object.keys(conditions ?? {}).forEach((conditionKey) => {
        if (conditionKey === "templateFunc") {
          resultConditions[conditionKey] = conditions[conditionKey];
        } else {
          resultConditions[conditionKey] = structuredClone(conditions[conditionKey]);
        }
      });

      queryLayout.conditions = resultConditions as IInsightQueryCondition;
    } else {
      queryLayout[key] = structuredClone(insight.queryLayout[key]);
    }
  });

  result.queryLayout = queryLayout as IInsightQueryLayout;

  return result as Insight;
};
